import type { PayloadAction } from '@reduxjs/toolkit';
import type { BlockOutWindow, RolesState, SettingsUsersState } from 'src/redux/settings-users/types';

// ---- Users (people) ----

/**
 * Block-out mutations write their own response into the cached GET instead of
 * refetching the whole user. Both reducers no-op unless the GET has already
 * built the entry — a `working_hours` invented here would have no `schedule`.
 */
const reducers = {
  upsertBlockOutWindow: (
    state: SettingsUsersState,
    action: PayloadAction<{ userId: string; window: BlockOutWindow }>,
  ) => {
    const { userId, window } = action.payload;
    const workingHours = state.source[userId]?.working_hours;
    if (!workingHours || !window?.technician_appointment_id) return;

    const windows = workingHours.block_out_windows ?? [];
    const existingIndex = windows.findIndex(
      (existing) => existing?.technician_appointment_id === window.technician_appointment_id,
    );

    // An edit replaces in place; a create appends. `BlockOutTimeList` sorts.
    if (existingIndex === -1) {
      workingHours.block_out_windows = [...windows, window];
    } else {
      workingHours.block_out_windows = windows.map((existing, index) =>
        index === existingIndex ? window : existing,
      );
    }
  },

  removeBlockOutWindow: (
    state: SettingsUsersState,
    action: PayloadAction<{ userId: string; windowId: string }>,
  ) => {
    const { userId, windowId } = action.payload;
    const workingHours = state.source[userId]?.working_hours;
    if (!workingHours?.block_out_windows) return;

    workingHours.block_out_windows = workingHours.block_out_windows.filter(
      (existing) => existing?.technician_appointment_id !== windowId,
    );
  },
};

export default reducers;

// ---- Roles (Settings → Users → User roles) ----

export const roleReducers = {
  updateRoleData: (
    state: RolesState,
    action: PayloadAction<{
      role_id: string;
      can_modify: boolean;
      description: string;
      name: string;
      parent_role_id: string;
      users_count: number;
    }>,
  ) => {
    const {
      role_id: roleId,
      can_modify: canModify,
      description,
      name,
      parent_role_id: parentRoleId,
      users_count: usersCount,
    } = action.payload;

    state.roles.data.data = [...(state.roles?.data?.data || []), roleId];

    state.roles.source = {
      ...(state.roles?.source || {}),
      [roleId]: {
        can_modify: canModify,
        description,
        name,
        parent_role_id: parentRoleId,
        users_count: usersCount,
        role_id: roleId,
      },
    };
  },

  deleteRoleFromRedux: (state: RolesState, action: PayloadAction<string>) => {
    const roleId = action.payload;
    const { data } = state.roles;
    state.roles.data.data = data?.data?.filter((role: string) => role !== roleId);
    state.roles.source = Object.fromEntries(
      Object.entries(state.roles.source).filter(([_key, role]) => role.role_id !== roleId),
    );
  },
};
